/**
 * 盘面状态（唯一事实源 + 层数据缓存）
 * - api 默认 RemoteClient；App 端（阶段 C）可 configureClient 注入 LocalClient
 * - 层数据按 key 缓存，重复切换不重复请求
 * - 历史记录（Android 客户端方案 A 节）：historyStorage 持久化输入参数，可注入（App=Preferences/Web=localStorage）
 */
import { create } from 'zustand';
import { RemoteClient } from '@shared/api/remote';
import type { ApiClient, BirthInput, LiuriDate, LiuriDaysResponse, ZizhanRequest } from '@shared/types/api';
import type { Base, Meta, Zhi } from '@shared/types/base';
import type { DaxianOverlay, LiunianOverlay, LiuriOverlay, LiushiOverlay, LiuyueMonth, LiuyueOverlay } from '@shared/types/overlay';
import type { LayerTab } from '@shared/render/layer-meta';
import type { LiuriDateInput } from '@shared/render';
import { LocalStorageStorage, MAX_HISTORY, DEFAULT_CATEGORY, chartInputKey, ANON_NS } from '@shared/engine/chart-store';
import type { CaseRecord, ChartStorage } from '@shared/engine/chart-store';
import {
  login as apiLogin,
  register as apiRegister,
  fetchCases as apiFetchCases,
  createCase as apiCreateCase,
  updateCase as apiUpdateCase,
  deleteCase as apiDeleteCase,
  fetchCategories as apiFetchCategories,
  putCategories as apiPutCategories,
  loadStoredAuth,
  storeAuth,
  clearStoredAuth,
  mergeCloudAndLocal,
} from '@shared/api/userCases';
import type { AuthUser } from '@shared/api/userCases';
import { jwtUserId } from '@shared/api/membership';

/** 层缓存：按 (layer, 参数) 存取 */
interface PackCache {
  daxian?: DaxianOverlay;
  liunian?: LiunianOverlay;
  liuyue?: LiuyueOverlay;
  /** 流日盘当月流月（单元素） */
  liuriMonth?: LiuyueMonth;
  liuri?: LiuriOverlay;
  /** 排盘流月序每日列表（农历；流日行候选日期） */
  liuriDays?: LiuriDaysResponse;
  liushi?: LiushiOverlay;
}

/**
 * 流日/流时「选中态」复位片段：清日期选中、时辰选中及 liuri/liushi 数据缓存。
 * 保留 liuriDays（流月候选日期列表）与 liuriMonth——取消流日时流月仍选中，
 * 流日行需继续展示该月全部日期（若一并清掉会退化成"请选择流月"）。
 */
function clearLiuriSelection(packs: PackCache): {
  liuriDate: LiuriDateInput;
  shiIndex: '';
  packs: PackCache;
} {
  return {
    liuriDate: { lunarYear: '', lunarMonth: '', lunarDay: '' },
    shiIndex: '',
    packs: { ...packs, liuri: undefined, liushi: undefined },
  };
}

/**
 * 流日/流时「整子树」复位片段：在选中态复位基础上，再清 liuriMonth/liuriDays。
 * 取消或切换上级层（大限/流年/流月）时必须一并复位，否则流日/流时行依赖
 * liuriDate/activeDay 渲染，会残留已取消的选中（历史 bug：取消大限后流时仍显示）。
 */
function clearLiuriSubtree(packs: PackCache): {
  liuriDate: LiuriDateInput;
  shiIndex: '';
  packs: PackCache;
} {
  return {
    ...clearLiuriSelection(packs),
    packs: { ...packs, liuri: undefined, liushi: undefined, liuriMonth: undefined, liuriDays: undefined },
  };
}

interface ChartState {
  // 客户端（可注入）
  api: ApiClient;
  // 建盘结果
  chartId: string | null;
  meta: Meta | null;
  base: Base | null;
  // 层选择参数
  layer: LayerTab;
  daxianOrder: number | '';
  year: number | '';
  liuyueMonth: number | '';
  /** 流日日期（可含空 = 当天；填全才请求指定日期） */
  liuriDate: LiuriDateInput;
  /** 选中流时辰序 0-11（空=未选） */
  shiIndex: number | '';
  // 层数据缓存
  packs: PackCache;
  loading: boolean;
  error: string | null;
  // 命例（持久化输入参数 + 名称/备注/分类；重启后点击命例重建盘）
  historyStorage: ChartStorage;
  cases: CaseRecord[];
  categories: string[];
  /** 命例页当前选中（列表高亮；排盘/修改/删除以此为准） */
  selectedCaseId: string | null;
  /** 紫占暂存例（不入库；开关关时供中宫姓名/时间显示，内存级） */
  transientCase: { name: string; input: BirthInput; hideBirth?: undefined } | null;
  /** 最近一次打开的命例 chartId（启动时恢复最近命盘） */
  lastChartId: string | null;
  /** 点击宫位高亮（飞星四化）：当前点击的宫位地支；再点同一宫取消 */
  activePalaceZhi: Zhi | null;
  /** 三方四正虚线起点（点击宫为命宫；null=当前层命宫）；选层时重置 */
  sanfangZhi: Zhi | null;
  /** 点击宫位：切换四化高亮 + 设虚线起点（再点同一宫仅取消四化，虚线不动） */
  clickPalace(zhi: Zhi): void;
  /** 清除点击高亮（选层时调用：四化高亮 + 虚线都重置） */
  clearPalaceHighlight(): void;
  // 登录 + 命例云同步（用户命例云端存储；未登录仅本地）
  /** ArmorClaw JWT（登录后有效；同时持久化到 localStorage paipan.auth） */
  token: string | null;
  /** 登录用户信息 */
  user: AuthUser | null;
  /** chartInputKey → 云端 UserCase.id（云端已知账本；持久化，供同步区分本地新增/他端删除 + 增删改定位） */
  cloudIdsByKey: Record<string, string>;
  /** syncCloud 进行中标记（防进页/回前台/登录并发重复拉取） */
  syncing: boolean;

  configureClient(api: ApiClient): void;
  /** 注入命例存储（App 端 @capacitor/preferences；默认 localStorage） */
  configureChartStorage(storage: ChartStorage): void;
  /** 从持久层恢复命例/分类/最近命例（启动时调用；同步本地客户端内存记录）；若已有登录态，后台拉云端合并 */
  loadCases(): Promise<void>;
  /** 登录：成功存 token/user 到本地并拉云端合并（云端为准 + 本地独有上传）；返回是否登录成功（云同步失败不阻塞登录） */
  login(email: string, password: string): Promise<boolean>;
  /** 注册：成功即登录（存 token/user + 拉云端合并）；失败抛错（携服务端文案，供页面展示） */
  register(username: string, email: string, password: string, confirm: string): Promise<void>;
  /** 退出：清 token，保留本地命例 */
  logout(): Promise<void>;
  /** 拉云端全量与本地合并（登录/启动时调用；无 token 直接返回；失败静默保留本地） */
  syncCloud(): Promise<void>;
  /** 选中命例（列表高亮） */
  selectCase(chartId: string): void;
  /** 新增命例（保存输入+标注，不改变当前盘面） */
  addCase(input: BirthInput, extra: { name: string; remark?: string; category?: string; hideBirth?: boolean }): Promise<void>;
  /** 修改命例（input 变化则重建 chartId/meta；标注变化直接更新） */
  updateCase(chartId: string, patch: { input?: BirthInput; name?: string; remark?: string; category?: string; hideBirth?: boolean }): Promise<void>;
  /** 删除单条命例 */
  removeCase(chartId: string): Promise<void>;
  /** 保存分类列表 */
  setCategories(categories: string[]): Promise<void>;
  /** 分类改名（该分类下命例同步更新） */
  renameCategory(oldName: string, newName: string): Promise<void>;
  /** 删除分类（该分类下命例归入默认分类） */
  deleteCategory(name: string): Promise<void>;
  /** 排盘：以命例输入重建盘（记录置顶 + 更新最近命例） */
  createChart(input: BirthInput): Promise<void>;
  /** 紫占起盘：persist=true 写入命例库（name 为 opts.name）；false 仅展示不入库（transientCase 兜底中宫显示）
   *  sixLayer=true：建盘后叠当前时刻限流盘（流日=今天农历 + 流时=当前时辰；大限层引擎自动落童限） */
  zizhanChart(req: ZizhanRequest, opts: { persist: boolean; name: string; sixLayer?: boolean }): Promise<void>;
  selectLayer(layer: LayerTab): Promise<void>;
  /** 底部大限表联动：切到大限层并选中该大限序号（清除下级选择） */
  selectDaxian(order: number): Promise<void>;
  /** 选中某流年：切到流年层（大限层之上） */
  selectLiunian(year: number): Promise<void>;
  /** 选中某流月：切到流月层（大限+流年之上） */
  selectLiuyueMonth(month: number): Promise<void>;
  /** 选中某流日（农历；lunarMonth 负数 = 闰月）：切到流日层 */
  selectLiuriDay(d: LiuriDate): Promise<void>;
  /** 选中某流时（时辰序 0-11）：叠加流时层 */
  selectLiushi(shiIndex: number): Promise<void>;
  /** 取消查看大限：切回本命层并清空序号 */
  clearDaxian(): void;
  /** 取消查看流年：回大限层 */
  clearLiunian(): void;
  /** 取消查看流月：回流年层 */
  clearLiuyue(): void;
  /** 取消查看流日：回流月层 */
  clearLiuri(): void;
  /** 取消查看流时：退回流日层 */
  clearLiushi(): void;
  setDaxianOrder(v: number | ''): Promise<void>;
  setYear(v: number | ''): Promise<void>;
  setLiuyueMonth(v: number | ''): Promise<void>;
  setLiuriDate(d: LiuriDateInput): Promise<void>;
  /** 内部：按当前层请求并刷新缓存 */
  refresh(): Promise<void>;
}

export const useChartStore = create<ChartState>((set, get) => ({
  api: new RemoteClient(),
  chartId: null,
  meta: null,
  base: null,
  layer: 'benming',
  daxianOrder: '',
  year: '',
  liuyueMonth: '',
  liuriDate: { lunarYear: '', lunarMonth: '', lunarDay: '' },
  shiIndex: '',
  activePalaceZhi: null,
  sanfangZhi: null,
  packs: {},
  loading: false,
  error: null,
  historyStorage: new LocalStorageStorage(),
  cases: [],
  categories: [DEFAULT_CATEGORY],
  selectedCaseId: null,
  transientCase: null,
  lastChartId: null,
  token: null,
  user: null,
  cloudIdsByKey: {},
  syncing: false,

  configureClient(api) {
    set({ api });
  },

  configureChartStorage(storage) {
    set({ historyStorage: storage });
  },

  async loadCases() {
    const auth = loadStoredAuth();
    // 当前身份命名空间：已登录=账号标识，未登录=匿名
    if (auth) set({ token: auth.token, user: auth.user });
    const ns = auth ? (accountKey(auth.token, auth.user) ?? ANON_NS) : ANON_NS;
    // 切到该命名空间并载入其本地桶（各账号缓存独立常驻，切换/登出均不删数据）
    await switchNamespace(ns);
    // 已登录：后台拉云端合并（不阻塞启动；失败保留本地）
    if (auth) void get().syncCloud();
  },

  async login(email, password) {
    try {
      const { token, user } = await apiLogin(email.trim(), password);
      storeAuth(token, user);
      set({ token, user });
      // 切到该账号命名空间（各账号本地缓存独立，切换不删数据）；并把匿名桶命例并入本账号
      const ns = accountKey(token, user) ?? ANON_NS;
      await switchNamespace(ns);
      await consumeAnonymousInto(ns);
      // 拉云端合并（内部容错：云不可用时仍登录成功，保留本地命例）
      await get().syncCloud();
      return true;
    } catch (e) {
      console.warn('[auth] 登录失败：', e);
      return false;
    }
  },

  async register(username, email, password, confirm) {
    // 注册接口抛错时携服务端 msg（如 email already registered），交由页面展示
    const { token, user } = await apiRegister(username.trim(), email.trim(), password, confirm);
    storeAuth(token, user);
    set({ token, user });
    // 切到新账号命名空间；把匿名桶命例并入本账号
    const ns = accountKey(token, user) ?? ANON_NS;
    await switchNamespace(ns);
    await consumeAnonymousInto(ns);
    await get().syncCloud();
  },

  async logout() {
    // 登出前尽力把本地未上传命例刷到云端，减少离线丢数据
    await get().syncCloud();
    clearStoredAuth();
    set({ token: null, user: null });
    // 身份切回匿名命名空间：账号桶原样保留（下次登录该账号即恢复），当前只显示匿名桶
    await switchNamespace(ANON_NS);
  },

  async syncCloud() {
    const token = get().token;
    if (!token || get().syncing) return;
    set({ syncing: true });
    const { historyStorage } = get();
    try {
      let cloud: { records: CaseRecord[]; idMap: Record<string, string> };
      try {
        cloud = await apiFetchCases(token);
      } catch (e) {
        console.warn('[cloud] 拉取云端命例失败：', e);
        return;
      }
      // 云端为准合并：账本区分「本地新增」（上传）与「他端已删」（本地丢弃），实现删除传播
      const { merged, ledger, localOnly } = mergeCloudAndLocal(
        get().cases,
        cloud.records,
        cloud.idMap,
        get().cloudIdsByKey,
      );
      // 本地真新增逐条上传（失败跳过，不阻塞本地）；成功后补入账本
      const nextLedger = { ...ledger };
      for (const r of localOnly) {
        try {
          const uc = await apiCreateCase(token, r);
          nextLedger[chartInputKey(r.input)] = uc.id;
        } catch (e) {
          console.warn('[cloud] 上传本地命例失败：', e);
        }
      }
      await historyStorage.save(merged);
      await historyStorage.saveCloudIds(nextLedger);
      set({ cases: merged, cloudIdsByKey: nextLedger });

      // 分类同步：结果 = 云端 ∪ 命例在用分类（不含本机残留的空分类 → 可传播他端删除）；有变化回写云端
      try {
        const cloudCats = await apiFetchCategories(token);
        const inUse = [...new Set(merged.map((r) => r.category?.trim() || DEFAULT_CATEGORY))];
        const cats: string[] = [];
        for (const c of [...cloudCats, ...inUse]) {
          if (c && !cats.includes(c)) cats.push(c);
        }
        const finalCats = cats.length ? cats : [DEFAULT_CATEGORY];
        await historyStorage.saveCategories(finalCats);
        set({ categories: finalCats });
        if (JSON.stringify(finalCats) !== JSON.stringify(cloudCats)) {
          await apiPutCategories(token, finalCats);
        }
      } catch (e) {
        console.warn('[cloud] 分类同步失败：', e);
      }
    } finally {
      set({ syncing: false });
    }
  },

  selectCase(chartId) {
    set({ selectedCaseId: chartId });
  },

  async addCase(input, extra) {
    const { api, historyStorage } = get();
    try {
      const created = await api.createChart(input);
      const base = await api.fetchBenming(created.chart_id);
      const record: CaseRecord = {
        chartId: created.chart_id,
        input,
        meta: created.meta,
        createdAt: Date.now(),
        name: extra.name.trim() || '匿名',
        remark: extra.remark?.trim() || undefined,
        category: extra.category || DEFAULT_CATEGORY,
        hideBirth: extra.hideBirth || undefined,
      };
      const next = [record, ...get().cases].slice(0, MAX_HISTORY);
      await historyStorage.save(next);
      await historyStorage.saveLastChartId(created.chart_id);
      // 同步当前盘为新建的命例（chartId/meta/base 一致，避免后续 daxian 用旧 chart_id）
      set({
        cases: next,
        selectedCaseId: record.chartId,
        chartId: created.chart_id,
        meta: created.meta,
        base,
        packs: {},
        layer: 'benming',
        daxianOrder: '',
        year: '',
        liuyueMonth: '',
        liuriDate: { lunarYear: '', lunarMonth: '', lunarDay: '' },
        shiIndex: '',
        loading: false,
        error: null,
      });
      // 云端同步（若已登录；失败不阻塞本地）
      const { token } = get();
      if (token) {
        try {
          const uc = await apiCreateCase(token, record);
          const nextLedger = { ...get().cloudIdsByKey, [chartInputKey(record.input)]: uc.id };
          set({ cloudIdsByKey: nextLedger });
          await historyStorage.saveCloudIds(nextLedger);
        } catch (e) {
          console.warn('[cloud] 新增命例同步失败：', e);
        }
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    }
  },

  async updateCase(chartId, patch) {
    const { api, historyStorage } = get();
    const target = get().cases.find((r) => r.chartId === chartId);
    if (!target) return;
    try {
      let record: CaseRecord;
      if (patch.input) {
        // 出生信息变化：重建盘（新 chartId/meta），替换原记录
        const created = await api.createChart(patch.input);
        (api as Partial<{ removeChart(cid: string): void }>).removeChart?.(chartId);
        record = {
          chartId: created.chart_id,
          input: patch.input,
          meta: created.meta,
          createdAt: Date.now(),
          name: patch.name?.trim() || target.name,
          remark: patch.remark !== undefined ? patch.remark.trim() || undefined : target.remark,
          category: patch.category || target.category || DEFAULT_CATEGORY,
          hideBirth: patch.hideBirth ?? target.hideBirth,
        };
      } else {
        record = {
          ...target,
          name: patch.name?.trim() || target.name,
          remark: patch.remark !== undefined ? patch.remark.trim() || undefined : target.remark,
          category: patch.category || target.category || DEFAULT_CATEGORY,
          hideBirth: patch.hideBirth ?? target.hideBirth,
        };
      }
      const next = get().cases.map((r) => (r.chartId === chartId ? record : r));
      await historyStorage.save(next);
      set({ cases: next, selectedCaseId: record.chartId });
      // 云端同步（若已登录；失败不阻塞本地）：以 chartInputKey 定位云端记录 id
      // - 账本已有旧 key 的云 id → 更新（input 变化则旧 key 迁移到新 key）
      // - 账本无（本地从未上传成功）→ 创建
      const { token, cloudIdsByKey } = get();
      if (token) {
        const oldKey = chartInputKey(target.input);
        const newKey = chartInputKey(record.input);
        const oldCloudId = cloudIdsByKey[oldKey];
        let nextLedger = cloudIdsByKey;
        try {
          if (oldCloudId) {
            await apiUpdateCase(token, oldCloudId, record);
            nextLedger = { ...cloudIdsByKey };
            delete nextLedger[oldKey];
            nextLedger[newKey] = oldCloudId;
          } else {
            const uc = await apiCreateCase(token, record);
            nextLedger = { ...cloudIdsByKey, [newKey]: uc.id };
          }
        } catch (e) {
          console.warn('[cloud] 修改命例同步失败：', e);
        }
        set({ cloudIdsByKey: nextLedger });
        await historyStorage.saveCloudIds(nextLedger);
      }
      // 修改的正是当前展示的命例：同步刷新盘面（若输入变化）
      if (patch.input && get().chartId === chartId) {
        await get().createChart(patch.input);
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    }
  },

  async removeCase(chartId) {
    const { cases, historyStorage, api } = get();
    const next = cases.filter((r) => r.chartId !== chartId);
    await historyStorage.save(next);
    (api as Partial<{ removeChart(cid: string): void }>).removeChart?.(chartId);
    const lastChartId = get().lastChartId === chartId ? (next[0]?.chartId ?? null) : get().lastChartId;
    await historyStorage.saveLastChartId(lastChartId);
    set({ cases: next, selectedCaseId: get().selectedCaseId === chartId ? null : get().selectedCaseId, lastChartId });
    // 云端同步删除（若已登录且账本有对应云 id；失败不阻塞本地）
    const target = cases.find((r) => r.chartId === chartId);
    const key = target ? chartInputKey(target.input) : '';
    const { token, cloudIdsByKey } = get();
    if (token && key && cloudIdsByKey[key]) {
      try {
        await apiDeleteCase(token, cloudIdsByKey[key]);
      } catch (e) {
        console.warn('[cloud] 删除命例同步失败：', e);
      }
      const nextLedger = { ...get().cloudIdsByKey };
      delete nextLedger[key];
      set({ cloudIdsByKey: nextLedger });
      await historyStorage.saveCloudIds(nextLedger);
    }
  },

  async setCategories(categories) {
    const cleaned = [...new Set(categories.map((c) => c.trim()).filter(Boolean))];
    const list = cleaned.length ? cleaned : [DEFAULT_CATEGORY];
    const { historyStorage } = get();
    await historyStorage.saveCategories(list);
    set({ categories: list });
    // 云端回写分类列表（若已登录；失败不阻塞本地）
    const { token } = get();
    if (token) {
      try {
        await apiPutCategories(token, list);
      } catch (e) {
        console.warn('[cloud] 分类上传失败：', e);
      }
    }
  },

  async renameCategory(oldName, newName) {
    const name = newName.trim();
    if (!name || name === oldName) return;
    const { cases, historyStorage, cloudIdsByKey, token } = get();
    const next = cases.map((r) => (r.category === oldName ? { ...r, category: name } : r));
    await historyStorage.save(next);
    const categories = [...new Set(get().categories.map((c) => (c === oldName ? name : c)).filter(Boolean))];
    const finalCats = categories.length ? categories : [DEFAULT_CATEGORY];
    await historyStorage.saveCategories(finalCats);
    set({ cases: next, categories: finalCats });
    // 云端：受影响命例逐条更新 + 分类列表回写（否则下次同步会被云端旧分类覆盖）
    if (token) {
      const affected = new Set(cases.filter((r) => r.category === oldName).map((r) => chartInputKey(r.input)));
      for (const r of next) {
        const id = affected.has(chartInputKey(r.input)) ? cloudIdsByKey[chartInputKey(r.input)] : undefined;
        if (!id) continue;
        try {
          await apiUpdateCase(token, id, r);
        } catch (e) {
          console.warn('[cloud] 分类改名同步失败：', e);
        }
      }
      try {
        await apiPutCategories(token, finalCats);
      } catch (e) {
        console.warn('[cloud] 分类上传失败：', e);
      }
    }
  },

  async deleteCategory(name) {
    const { cases, historyStorage, cloudIdsByKey, token } = get();
    const next = cases.map((r) => (r.category === name ? { ...r, category: DEFAULT_CATEGORY } : r));
    await historyStorage.save(next);
    const categories = get().categories.filter((c) => c !== name);
    const finalCats = categories.length ? categories : [DEFAULT_CATEGORY];
    await historyStorage.saveCategories(finalCats);
    set({ cases: next, categories: finalCats });
    // 云端：被归入默认分类的命例逐条更新 + 分类列表回写
    if (token) {
      const affected = new Set(cases.filter((r) => r.category === name).map((r) => chartInputKey(r.input)));
      for (const r of next) {
        const id = affected.has(chartInputKey(r.input)) ? cloudIdsByKey[chartInputKey(r.input)] : undefined;
        if (!id) continue;
        try {
          await apiUpdateCase(token, id, r);
        } catch (e) {
          console.warn('[cloud] 分类删除同步失败：', e);
        }
      }
      try {
        await apiPutCategories(token, finalCats);
      } catch (e) {
        console.warn('[cloud] 分类上传失败：', e);
      }
    }
  },

  async createChart(input) {
    const { api, historyStorage } = get();
    set({ loading: true, error: null });
    try {
      const created = await api.createChart(input);
      const base = await api.fetchBenming(created.chart_id);
      // 命例：相同输入去重（保留原记录的名称/备注/分类/隐藏生辰标注）并置顶，上限 MAX_HISTORY 删最旧
      const cases = get().cases;
      const key = chartInputKey(input);
      const old = cases.find((r) => chartInputKey(r.input) === key);
      const record: CaseRecord = {
        chartId: created.chart_id,
        input,
        meta: created.meta,
        createdAt: Date.now(),
        name: old?.name || `命例 ${input.year}-${String(input.month).padStart(2, '0')}-${String(input.day).padStart(2, '0')}`,
        remark: old?.remark,
        category: old?.category || DEFAULT_CATEGORY,
        // 排盘/刷新/启动恢复都会重建记录：不保留则 hideBirth 被抹掉（updateCase 保存后也会触发 createChart）
        hideBirth: old?.hideBirth,
      };
      const next = [record, ...cases.filter((r) => r.chartId !== old?.chartId)].slice(0, MAX_HISTORY);
      await historyStorage.save(next);
      await historyStorage.saveLastChartId(created.chart_id);
      set({
        chartId: created.chart_id,
        meta: created.meta,
        base,
        packs: {},
        layer: 'benming',
        daxianOrder: '',
        year: '',
        liuyueMonth: '',
        liuriDate: { lunarYear: '', lunarMonth: '', lunarDay: '' },
        shiIndex: '',
        cases: next,
        selectedCaseId: created.chart_id,
        lastChartId: created.chart_id,
        loading: false,
      });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  async zizhanChart(req, opts) {
    const { api, historyStorage } = get();
    set({ loading: true, error: null });
    try {
      const created = await api.zizhan(req);
      const base = await api.fetchBenming(created.chart_id);
      const reset = {
        chartId: created.chart_id,
        meta: created.meta,
        base,
        packs: {},
        layer: 'benming' as const,
        daxianOrder: '' as const,
        year: '' as const,
        liuyueMonth: '' as const,
        liuriDate: { lunarYear: '' as const, lunarMonth: '' as const, lunarDay: '' as const },
        shiIndex: '' as const,
        selectedCaseId: created.chart_id,
        loading: false,
      };
      if (opts.persist) {
        const record: CaseRecord = {
          chartId: created.chart_id,
          input: created.input,
          meta: created.meta,
          createdAt: Date.now(),
          name: opts.name.trim() || '紫占命例',
          category: DEFAULT_CATEGORY,
        };
        const next = [record, ...get().cases].slice(0, MAX_HISTORY);
        await historyStorage.save(next);
        await historyStorage.saveLastChartId(created.chart_id);
        set({ ...reset, cases: next, lastChartId: created.chart_id, transientCase: null });
        // 云端同步（若已登录；失败不阻塞本地）
        const { token } = get();
        if (token) {
          try {
            const uc = await apiCreateCase(token, record);
            const nextLedger = { ...get().cloudIdsByKey, [chartInputKey(record.input)]: uc.id };
            set({ cloudIdsByKey: nextLedger });
            await historyStorage.saveCloudIds(nextLedger);
          } catch (e) {
            console.warn('[cloud] 新增命例同步失败：', e);
          }
        }
      } else {
        // 不入库：仅内存暂存（中宫姓名/时间显示兜底），不动 cases/lastChartId
        set({ ...reset, transientCase: { name: opts.name, input: created.input } });
      }
      // 当前时刻限流盘：叠流日（今天农历）+ 流时（当前时辰）；出生=现在 ⇒ 虚岁 1 < 起运 ⇒ 大限层自动落童限
      if (opts.sixLayer) {
        try {
          const curYear = new Date().getFullYear();
          // 并行：今日流日（缺省=当天）+ 当年完整流月列表（底部流月行渲染依赖 packs.liuyue）
          const [liuriRes, liuyueRes] = await Promise.all([
            api.fetchLiuri(created.chart_id),
            api.fetchLiuyue(created.chart_id, curYear),
          ]);
          const liuriToday = liuriRes.overlay.liuri[0];
          const [ly, lm, ld] = liuriToday.lunar;
          // 底部联动区完整选中态：大限=0（童限，DaxianTable 以 activeOrder===0 放行）+ 流月号（今日所属流月）
          const wantLunarMonth = liuriToday.leap ? -lm : lm;
          const months = liuyueRes.overlay.liuyue;
          const monthNo = months.months.find((m) => m.lunarMonth === wantLunarMonth)?.month ?? '';
          const shiIndex = Math.floor(((new Date().getHours() + 1) % 24) / 2); // 23 点归子时 0
          set({
            layer: 'liuri',
            daxianOrder: 0,
            year: curYear,
            liuyueMonth: monthNo,
            liuriDate: { lunarYear: ly, lunarMonth: lm, lunarDay: ld },
            shiIndex,
            packs: { ...get().packs, liuyue: months },
          });
          await get().refresh();
        } catch (e) {
          // 叠层失败不阻塞：降级本命层展示
          console.warn('[zizhan] 限流盘叠层失败，降级本命层：', e);
        }
      }
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  async selectLayer(layer) {
    set({ layer, activePalaceZhi: null, sanfangZhi: null });
    await get().refresh();
  },

  async selectDaxian(order) {
    const { packs } = get();
    // 切换大限：复位下级全部选中（流年/流月/流日/流时）+ 清点击宫高亮
    set({ layer: 'daxian', daxianOrder: order, year: '', liuyueMonth: '', ...clearLiuriSubtree(packs), activePalaceZhi: null, sanfangZhi: null });
    await get().refresh();
  },

  async selectLiunian(year) {
    const { packs } = get();
    // 切换流年：复位下级全部选中（流月/流日/流时）+ 清点击宫高亮
    set({ layer: 'liunian', year, liuyueMonth: '', ...clearLiuriSubtree(packs), activePalaceZhi: null, sanfangZhi: null });
    await get().refresh();
  },

  async selectLiuyueMonth(month) {
    const { packs } = get();
    // 切换流月：复位下级全部选中（流日/流时）+ 清点击宫高亮
    set({ layer: 'liuyue', liuyueMonth: month, ...clearLiuriSubtree(packs), activePalaceZhi: null, sanfangZhi: null });
    await get().refresh();
  },

  async selectLiuriDay(d) {
    const { packs } = get();
    // 换流日：清掉旧日的流时缓存 + 清点击宫高亮
    set({ layer: 'liuri', liuriDate: d, shiIndex: '', packs: { ...packs, liushi: undefined }, activePalaceZhi: null, sanfangZhi: null });
    await get().refresh();
  },

  async selectLiushi(shiIndex) {
    set({ layer: 'liuri', shiIndex, activePalaceZhi: null, sanfangZhi: null });
    await get().refresh();
  },

  clickPalace(zhi) {
    if (get().activePalaceZhi === zhi) {
      // 再点同一宫：仅取消四化高亮；虚线不动（保持点击宫的 sanfang）
      set({ activePalaceZhi: null });
    } else {
      set({ activePalaceZhi: zhi, sanfangZhi: zhi });
    }
  },

  clearPalaceHighlight() {
    set({ activePalaceZhi: null, sanfangZhi: null });
  },

  clearDaxian() {
    const { packs } = get();
    // 取消大限：级联复位流年/流月/流日/流时（含缓存）+ 清点击宫高亮
    set({ layer: 'benming', daxianOrder: '', year: '', liuyueMonth: '', ...clearLiuriSubtree(packs), activePalaceZhi: null, sanfangZhi: null });
  },

  clearLiunian() {
    const { packs } = get();
    // 取消流年：级联复位流月/流日/流时（含缓存）+ 清点击宫高亮
    set({ layer: 'daxian', year: '', liuyueMonth: '', ...clearLiuriSubtree(packs), activePalaceZhi: null, sanfangZhi: null });
  },

  clearLiuyue() {
    const { packs } = get();
    // 取消流月：级联复位流日/流时（含缓存）+ 清点击宫高亮
    set({ layer: 'liunian', liuyueMonth: '', ...clearLiuriSubtree(packs), activePalaceZhi: null, sanfangZhi: null });
  },

  clearLiuri() {
    const { packs } = get();
    // 取消流日：只清流日/流时选中态与数据缓存 + 清点击宫高亮
    set({ layer: 'liuyue', ...clearLiuriSelection(packs), activePalaceZhi: null, sanfangZhi: null });
  },

  clearLiushi() {
    const { packs } = get();
    // 仅清选中还不够：activeLayers 只要有 packs.liushi 就叠加流时四化，必须同步清缓存 + 清点击宫高亮
    set({ shiIndex: '', packs: { ...packs, liushi: undefined }, activePalaceZhi: null, sanfangZhi: null });
  },

  async setDaxianOrder(v) {
    set({ daxianOrder: v });
    await get().refresh();
  },

  async setYear(v) {
    set({ year: v });
    await get().refresh();
  },

  async setLiuyueMonth(v) {
    set({ liuyueMonth: v });
    await get().refresh();
  },

  async setLiuriDate(d) {
    set({ liuriDate: d });
    await get().refresh();
  },

  // ---- 内部：按当前层请求并缓存 ----
  async refresh() {
    const { api, chartId, layer, daxianOrder, year, liuyueMonth, liuriDate, shiIndex, packs } = get();
    if (!chartId || layer === 'benming') return;
    set({ loading: true, error: null });
    try {
      if (layer === 'daxian') {
        const res = await api.fetchDaxian(chartId, daxianOrder === '' ? undefined : daxianOrder);
        set({ packs: { ...packs, daxian: res.overlay.daxian[0] } });
      } else if (layer === 'liunian') {
        // 并行拉流年 + 流月（后者供底部流月行干支展示）
        const [res, ly] = await Promise.all([
          api.fetchLiunian(chartId, year === '' ? undefined : year),
          api.fetchLiuyue(chartId, year === '' ? undefined : year),
        ]);
        set({
          packs: {
            ...packs,
            daxian: res.overlay.daxian[0],
            liunian: res.overlay.liunian[0],
            liuyue: ly.overlay.liuyue,
          },
        });
      } else if (layer === 'liuyue') {
        const res = await api.fetchLiuyue(chartId, year === '' ? undefined : year);
        const liuyue = res.overlay.liuyue;
        // 选中排盘月 → 拉该月每日列表（流日行候选日期）；同 (农历年, 排盘月) 复用缓存
        let days: LiuriDaysResponse | undefined;
        if (liuyueMonth !== '') {
          const want = { lunarYear: liuyue.lunarYear, lunarMonth: liuyueMonth };
          const cached = packs.liuriDays;
          if (cached?.lunarYear === want.lunarYear && cached.lunarMonth === want.lunarMonth) days = cached;
          else days = await api.fetchLiuriDays(chartId, want.lunarYear, want.lunarMonth);
        }
        set({
          packs: {
            ...packs,
            daxian: res.overlay.daxian[0],
            liunian: res.overlay.liunian[0],
            liuyue,
            ...(days ? { liuriDays: days } : {}),
          },
        });
      } else if (layer === 'liuri') {
        const full: LiuriDate | undefined =
          liuriDate.lunarYear !== '' && liuriDate.lunarMonth !== '' && liuriDate.lunarDay !== ''
            ? {
                lunarYear: liuriDate.lunarYear,
                lunarMonth: liuriDate.lunarMonth,
                lunarDay: liuriDate.lunarDay,
              }
            : undefined;
        const res = await api.fetchLiuri(chartId, full);
        const liuri = res.overlay.liuri[0];
        const luMonth = res.overlay.liuyue[0];
        // 该农历月的每日列表（农历年 = liuri.lunar[0]；农历月号 = liuyue.lunarMonth，负数=闰月）
        const want = { lunarYear: liuri.lunar[0], lunarMonth: luMonth.lunarMonth };
        const cached = packs.liuriDays;
        const days =
          cached?.lunarYear === want.lunarYear && cached.lunarMonth === want.lunarMonth
            ? cached
            : await api.fetchLiuriDays(chartId, want.lunarYear, want.lunarMonth);

        // 已选流时 → 拉流时 overlay；未选 → 显式置空（不用 ...packs 展开残留旧缓存）
        let liushiPack: LiushiOverlay | undefined;
        if (shiIndex !== '' && full) {
          try {
            const liushiRes = await api.fetchLiushi(chartId, {
              lunarYear: full.lunarYear,
              lunarMonth: full.lunarMonth,
              lunarDay: full.lunarDay,
              shichen: shiIndex,
            });
            liushiPack = liushiRes.overlay.liushi[0];
          } catch {
            // 流时拉取失败不影响已有数据
          }
        }

        const newPacks: PackCache = {
          ...packs,
          daxian: res.overlay.daxian[0],
          liunian: res.overlay.liunian[0],
          liuriMonth: luMonth,
          liuri: res.overlay.liuri[0],
          liuriDays: days,
          liushi: liushiPack,
        };

        set({ packs: newPacks });
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      set({ loading: false });
    }
  },
}));

/** 账号身份标识：优先 JWT user_id，其次登录邮箱；都取不到返回 null（视为匿名） */
function accountKey(token: string | null, user: AuthUser | null | undefined): string | null {
  const uid = jwtUserId(token);
  if (uid != null) return `uid:${uid}`;
  const em = user?.email?.trim().toLowerCase();
  if (em) return `email:${em}`;
  return null;
}

/**
 * 切换当前命名空间并载入其本地桶：各账号/匿名的命例数据独立常驻，切换与登出都不删除任何桶。
 * 同时重置内存态「当前盘面」（切桶后旧盘面不再属于当前身份；仅内存，不动持久化）。
 */
async function switchNamespace(ns: string): Promise<void> {
  const { historyStorage, api } = useChartStore.getState();
  await historyStorage.useNamespace(ns);
  const [records, categories, lastChartId, cloudIdsByKey] = await Promise.all([
    historyStorage.load(),
    historyStorage.loadCategories(),
    historyStorage.loadLastChartId(),
    historyStorage.loadCloudIds(),
  ]);
  // App 端 LocalClient 内存同步（chartId → 输入记录，取用时懒构建）
  (api as Partial<{ restore(r: CaseRecord[]): void }>).restore?.(records);
  useChartStore.setState({
    cases: records,
    categories,
    lastChartId,
    cloudIdsByKey,
    selectedCaseId: null,
    chartId: null,
    meta: null,
    base: null,
    layer: 'benming',
    daxianOrder: '',
    year: '',
    liuyueMonth: '',
    liuriDate: { lunarYear: '', lunarMonth: '', lunarDay: '' },
    shiIndex: '',
    packs: {},
    activePalaceZhi: null,
    sanfangZhi: null,
    transientCase: null,
    error: null,
  });
}

/**
 * 登录到账号 ns 时消费匿名桶：把未登录期间新建的命例并入该账号（按 chartInputKey 去重），
 * 随后清空匿名桶（匿名数据一次性归入账号，避免被后续其它账号重复并入）。账号桶永不删除。
 * 并入的命例在随后 syncCloud 中作为「本地新增」上传到该账号云端。
 */
async function consumeAnonymousInto(ns: string): Promise<void> {
  if (ns === ANON_NS) return;
  const st = useChartStore.getState();
  const { historyStorage } = st;
  const anonCases = await historyStorage.peekCases(ANON_NS);
  if (!anonCases.length) return;
  const existing = new Set(st.cases.map((r) => chartInputKey(r.input)));
  const toAdd = anonCases.filter((r) => !existing.has(chartInputKey(r.input)));
  if (toAdd.length) {
    const merged = [...toAdd, ...st.cases].slice(0, MAX_HISTORY);
    await historyStorage.save(merged);
    useChartStore.setState({ cases: merged });
  }
  await historyStorage.clearNamespace(ANON_NS);
}

/** 供 App 端注入本地客户端（阶段 C） */
export function configureClient(api: ApiClient) {
  useChartStore.getState().configureClient(api);
}

/** 供 App 端注入历史存储（@capacitor/preferences；默认 localStorage） */
export function configureChartStorage(storage: ChartStorage) {
  useChartStore.getState().configureChartStorage(storage);
}
